/*
  <glossary-rows> — GB. Deferred, self-registering.

  Every row is server rendered OPEN, because a <details> serves its content
  through a shadow slot and CSS cannot force a closed one open. Rendering open
  means the desktop table is correct with no JavaScript and never shifts on
  load; this element's only job is to CLOSE the rows below 750px, where the
  design is an accordion.

  Deferred execution runs before first paint, so there is no flash. With
  JavaScript unavailable the glossary reads as a fully expanded list, which is
  the correct fallback for reference content.
*/
if (!customElements.get('glossary-rows')) {
  customElements.define(
    'glossary-rows',
    class GlossaryRows extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.rows = Array.from(this.querySelectorAll('details'));
        if (!this.rows.length) return;

        this.desktop = window.matchMedia('(min-width: 750px)');
        this.sync();
        this.desktop.addEventListener('change', () => this.sync());
      }

      sync() {
        const isDesktop = this.desktop.matches;
        this.rows.forEach((row) => {
          // Desktop: always open, and the summary is inert via CSS.
          // Mobile: closed by default, tap to open, like Collapsible Content.
          row.open = isDesktop;
        });
      }
    }
  );
}

/*
  <glossary-filter> — GB. Alphabet filter for the Glossary section.

  Filtering is a class toggle over rows already in the DOM, so there is no
  fetch, no re-render and no layout thrash. Rows carry data-letter stamped by
  Liquid, so the JS never has to parse or guess at the term text.

  Fail open, in two senses:
  - the bar is hidden by CSS until this element adds .is-ready, so a script
    that never runs leaves every row visible rather than a dead control bar.
  - .is-ready is set the moment the CORE click filtering is wired, BEFORE the
    optional enhancements below it. If grab-to-scroll or the centring ever
    throws, the filter still renders and still works.

  View All is the state on load.
*/
if (!customElements.get('glossary-filter')) {
  customElements.define(
    'glossary-filter',
    class GlossaryFilter extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.buttons = Array.from(this.querySelectorAll('.glossary__letter'));
        this.rows = Array.from(this.querySelectorAll('.glossary__row'));
        this.status = this.querySelector('.glossary__status');
        this.track = this.querySelector('.glossary__letters');
        if (!this.buttons.length || !this.rows.length) return;

        this.addEventListener('click', (event) => {
          const button = event.target.closest('.glossary__letter');
          if (!button || button.disabled) return;
          this.apply(button);
        });

        // Core is live from here. Everything after this point is optional.
        this.classList.add('is-ready');

        this.trackHeader();
        this.bindGrab();
        this.reveal(this.querySelector('.glossary__letter.is-active'));
      }

      /* The pin line for the sticky letter bar: the bottom edge of the
         sticky header. Mirrors headerBottom() in sticky-add-to-cart.js so
         the two controls park the same distance under the same edge.

         MEASURED, NOT DERIVED, for the reason that file gives. The theme
         does publish --header-height and --header-area-margin, and the
         first version of this used them, but --header-height under-reports
         the rendered mobile header by about 8px, which lands straight in an
         11px gap. Reading the real element is correct whatever the logo
         size, the announcement bar or the header margin are doing.

         Returns 0 when the header is not sticky, because then there is
         nothing overhead to clear. The CSS falls back to the derived value
         if this never runs, so the bar still clears the header without JS. */
      headerBottom() {
        const header = document.querySelector('sticky-header');
        if (!header || !header.isConnected) return 0;

        const section = header.closest('.shopify-section') || header.parentElement;
        if (!section) return 0;

        const styles = getComputedStyle(section);
        if (styles.position !== 'sticky' && styles.position !== 'fixed') return 0;

        return header.getBoundingClientRect().height + (parseFloat(styles.top) || 0);
      }

      measureHeader() {
        this.style.setProperty('--gb-gloss-header', `${this.headerBottom()}px`);
      }

      /* Re-measured on resize AND whenever the header itself changes size.
         That second one is not optional here: this store's header uses the
         reduce-logo-size sticky type, so the header is shorter once the page
         is scrolled, which is exactly when the bar is pinned. */
      trackHeader() {
        this.measureHeader();

        /* Two re-measures that do not depend on ResizeObserver.

           The observer below is the general mechanism, but it only reports
           during a rendering step, and that step is exactly what a browser
           suspends in a background tab. The one change that reliably happens
           after this first measurement is the web font arriving: on a phone
           it grows the header from about 102px to 110px, and a missed update
           leaves the bar 3px under the header instead of 11px. fonts.ready
           is the event for that change, so it is measured directly. The load
           event is a backstop for anything else late, such as a logo image.
           Both are one-shot and cost nothing afterwards. */
        if (document.fonts && document.fonts.ready) {
          document.fonts.ready.then(() => this.measureHeader());
        }
        if (document.readyState !== 'complete') {
          window.addEventListener('load', () => this.measureHeader(), { once: true });
        }

        const header = document.querySelector('sticky-header');
        if (!header || typeof ResizeObserver === 'undefined') return;

        this.headerObserver = new ResizeObserver(() => this.measureHeader());
        this.headerObserver.observe(header);
      }

      disconnectedCallback() {
        if (this.headerObserver) this.headerObserver.disconnect();
      }

      apply(button) {
        const letter = button.dataset.letter || '';

        this.buttons.forEach((b) => {
          const on = b === button;
          b.classList.toggle('is-active', on);
          b.setAttribute('aria-pressed', on ? 'true' : 'false');
        });

        let shown = 0;
        this.rows.forEach((row) => {
          const match = !letter || row.dataset.letter === letter;
          row.classList.toggle('is-filtered-out', !match);
          if (match) shown += 1;
        });

        this.reveal(button);
        this.anchor();

        // Announce the result rather than changing the page silently.
        if (this.status) {
          this.status.textContent = letter
            ? `${shown} ${shown === 1 ? 'term' : 'terms'} starting with ${letter}`
            : `Showing all ${shown} terms`;
        }
      }

      /* Put the visitor back on the letter bar after a filter.

         THE PROBLEM. Filtering hides rows, so the section can collapse from
         48,000px to a few hundred. If the visitor was deep inside it when
         they clicked, their scroll position no longer exists, the browser
         clamps it to the new page bottom, and they land in the footer looking
         at nothing they asked for. The deeper they were and the fewer the
         matches, the worse it is, so the letters with the least behind them
         fail the hardest.

         THE LANDING. Scroll so the bar sits exactly on its own pin line, with
         the first match directly beneath it. The bar stays put, which is the
         point: they can pick another letter without hunting for the control.

         WHY THE PIN LINE IS READ, NOT RECALCULATED. The CSS already resolves
         it from the measured header, so reading `top` back off the element is
         one source of truth. Same approach sticky-add-to-cart.js takes with
         its own pin line, and it means this lands correctly at any width and
         any header height without knowing either.

         ONLY EVER SCROLLS UP. If the visitor is above the landing line, they
         have not reached the bar yet and nothing should yank them down; the
         guard makes it a no-op. Instant, not smooth, because the distance can
         be tens of thousands of pixels and a smooth trip over that reads as a
         broken page rather than a considered one. */
      anchor() {
        const bar = this.querySelector('.glossary__filter');
        if (!bar) return;

        // Measure now rather than trust a stored value, so the landing is
        // right at the moment it happens. getComputedStyle below forces the
        // style recalculation, so it reads the value just written.
        this.measureHeader();

        const pin = parseFloat(getComputedStyle(bar).top) || 0;
        const landing = Math.max(0, Math.round(this.getBoundingClientRect().top + window.scrollY - pin));

        if (window.scrollY > landing) window.scrollTo(0, landing);
      }

      /*
        Desktop grab-to-scroll, ported from section-collection-list-banner.js
        so both horizontal tracks in this theme behave identically.

        Why it is needed: a trackpad can swipe horizontally, a plain mouse
        wheel cannot. Without this a desktop mouse user has no way to reach
        the far end of the alphabet.

        Mouse only — the pointerType check leaves touch and pen on the native
        scrolling they already have. A 6px threshold means a normal click on a
        letter is never mistaken for a drag, and the click that follows a real
        drag is swallowed so releasing over "M" does not silently filter to M.
      */
      bindGrab() {
        const track = this.track;
        if (!track) return;

        const THRESHOLD = 6;
        let pointerId = null;
        let dragging = false;
        let startX = 0;
        let startLeft = 0;
        let suppressClick = false;

        track.addEventListener('pointerdown', (event) => {
          if (event.pointerType !== 'mouse' || event.button !== 0) return;
          if (track.scrollWidth <= track.clientWidth) return;
          pointerId = event.pointerId;
          dragging = false;
          startX = event.clientX;
          startLeft = track.scrollLeft;
        });

        track.addEventListener('pointermove', (event) => {
          if (pointerId === null || event.pointerId !== pointerId) return;
          const delta = event.clientX - startX;
          if (!dragging) {
            if (Math.abs(delta) < THRESHOLD) return;
            dragging = true;
            track.classList.add('is-grabbing');
            try {
              track.setPointerCapture(pointerId);
            } catch (error) {
              // Pointer already gone — the drag still works.
            }
          }
          event.preventDefault();
          track.scrollLeft = startLeft - delta;
        });

        const release = (event) => {
          if (pointerId === null || event.pointerId !== pointerId) return;
          if (dragging) {
            suppressClick = true;
            track.classList.remove('is-grabbing');
            try {
              track.releasePointerCapture(pointerId);
            } catch (error) {
              // Capture already released — nothing to undo.
            }
            setTimeout(() => {
              suppressClick = false;
            }, 0);
          }
          pointerId = null;
          dragging = false;
        };

        track.addEventListener('pointerup', release);
        track.addEventListener('pointercancel', release);

        // Native text/element drag would hijack the grab mid-press.
        track.addEventListener('dragstart', (event) => {
          if (pointerId !== null || dragging) event.preventDefault();
        });

        // Swallow the click that immediately follows a drag release.
        track.addEventListener(
          'click',
          (event) => {
            if (suppressClick) {
              event.preventDefault();
              event.stopPropagation();
              suppressClick = false;
            }
          },
          true
        );
      }

      /*
        Bring the active letter into view, centred where possible. Ported from
        multicolumn-tabs.js so the Quality page tab bar and this filter behave
        identically. scrollTo on the track only — never scrollIntoView, which
        would drag the whole document. View All lives outside the track, so
        there is nothing to scroll to for it.
      */
      reveal(button) {
        if (!this.track || !button || !this.track.contains(button)) return;
        const overflow = this.track.scrollWidth - this.track.clientWidth;
        if (overflow <= 0) return;
        const target = button.offsetLeft - (this.track.clientWidth - button.offsetWidth) / 2;
        const left = Math.max(0, Math.min(target, overflow));
        const motionOk = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.track.scrollTo({ left: left, behavior: motionOk ? 'smooth' : 'auto' });
      }
    }
  );
}
